import type { MouseEventHandler } from "react";
import { useDeferredValue, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, Modal, Tooltip } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks";

import { mnpTrademarkNavigation } from "../../app/navigation";
import { useDeleteTrademark } from "../../services/api/trademarks";
import { trademarkAssetTracking } from "../../services/tracking/asset-management";
import { DomainBanner } from "../../shared/components/domain-banner";
import { Toast } from "../../shared/ui/toast";

export const DeleteTrademark = (props: {
    canDelete?: boolean;
    trademarkId?: string;
    trademarkName?: string;
    domain?: string;
}) => {
    const { canDelete = false, trademarkId, trademarkName, domain } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toasts = useToasts();
    const { translate } = useTranslate();
    const { mutateAsync: deleteTrademark, isPending } = useDeleteTrademark();
    const [trademarkNameInput, setTrademarkNameInput] = useState("");
    const canDeleteConfirmed = useDeferredValue(trademarkName === trademarkNameInput);

    const confirmDeletion: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
    };

    const handleDeleteTrademark = () => {
        if (trademarkId) {
            void deleteTrademark([trademarkId])
                .then(() => {
                    trademarkAssetTracking.onDeleted({
                        id: trademarkId,
                        name: trademarkName,
                    });
                    toasts.showSuccessToast(
                        <Toast
                            text={translate("monitor_and_protect.features.trademark_config_list.delete_success_toast")}
                        />,
                    );
                    void mnpTrademarkNavigation.trademarks.go();
                })
                .catch(() => {
                    toasts.showErrorToast(
                        <Toast
                            type="error"
                            text={translate("monitor_and_protect.features.trademark_config_list.delete_error_toast")}
                        />,
                    );
                })
                .finally(() => setIsModalOpen(false));
        }
    };

    return (
        <Box>
            <Tooltip
                enabled={!canDelete}
                content={translate(
                    canDelete
                        ? "monitor_and_protect.features.trademark_config_list.delete_tooltip"
                        : "monitor_and_protect.features.trademark_config_list.delete_permission_tooltip",
                )}
            >
                <Button
                    type="flatWarning"
                    isDisabled={!canDelete || isPending}
                    onClick={confirmDeletion}
                    isLoading={isPending}
                >
                    Delete
                </Button>
            </Tooltip>
            <Box onClick={(e) => e.stopPropagation()}>
                <Modal
                    isOpen={isModalOpen}
                    onRequestClose={handleClose}
                >
                    <Modal.CloseButton onClick={handleClose} />
                    <Modal.Header>
                        <Text fontVariant="h5_2">
                            {translate("monitor_and_protect.features.trademark_config_list.delete_modal.title")}
                        </Text>
                    </Modal.Header>
                    <Modal.Content
                        gap="24px"
                        paddingY="0 24px"
                    >
                        <DomainBanner
                            domain={domain}
                            name={trademarkName}
                        />
                        <TextField
                            label={translate(
                                "monitor_and_protect.features.trademark_config_list.delete_modal.confirm_label",
                            )}
                            onChange={(value) => setTrademarkNameInput(value)}
                        />
                    </Modal.Content>
                    <Modal.Footer>
                        <Button
                            type="flat"
                            onClick={handleClose}
                        >
                            {translate("monitor_and_protect.features.trademark_config_list.delete_modal.cancel_button")}
                        </Button>
                        <Button
                            type="warning"
                            isDisabled={!canDeleteConfirmed || isPending}
                            onClick={handleDeleteTrademark}
                            isLoading={isPending}
                        >
                            {translate("monitor_and_protect.features.trademark_config_list.delete_modal.delete_button")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Box>
        </Box>
    );
};
